/**
 * TaxableValueOverrideModal - Modal for customizing taxable value growth rates
 *
 * Allows users to override specific year growth rates for fine-tuning.
 * When a year's growth rate is changed, all subsequent years recalculate
 * to show the cascading effect. Values update on blur for live preview.
 *
 * Creates a new configuration with the overrides applied when submitted.
 */

import { Box, Button, Dialog, HStack, Input, Stack, Table, Text, VStack } from '@chakra-ui/react';
import { RefreshCw, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { projectTaxableValues } from '~/builders/taxable-value/TaxableValueHelper';
import {
  removeTaxableValueOverride,
  setTaxableValueGrowthRateOverride,
  validateTaxableValueGrowthRateOverride,
} from '~/services/override-service';
import type { TaxValueInputs } from '~/types';
import type { TaxableValueOverrideRow, TaxableValueOverrides } from '~/types/overrides';

// ============================================================================
// Types
// ============================================================================

export interface TaxableValueOverrideModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler called when modal should close */
  onClose: () => void;
  /** Handler called when user submits overrides */
  onSubmit: (overrides: TaxableValueOverrides) => void;
  /** The tax value inputs (for calculating default growth rates and projections) */
  taxValueInputs: TaxValueInputs;
  /** Starting tax year for the projection */
  startingTaxYear: number;
  /** Number of years to show in the table */
  yearsToShow?: number;
  /** Existing overrides to pre-populate (for editing) */
  existingOverrides?: TaxableValueOverrides | null;
}

// ============================================================================
// Utility Functions
// ============================================================================

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatGrowthRate = (rate: number): string => {
  // rate is decimal (0.035 for 3.5%)
  return `${(rate * 100).toFixed(2)}%`;
};

const parseGrowthRateInput = (input: string): number | null => {
  // Remove % sign and whitespace
  const cleaned = input.replace(/%/g, '').trim();
  const parsed = Number.parseFloat(cleaned);
  if (Number.isNaN(parsed)) return null;
  // Convert percentage to decimal
  return parsed / 100;
};

// ============================================================================
// Component
// ============================================================================

export const TaxableValueOverrideModal: React.FC<TaxableValueOverrideModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  taxValueInputs,
  startingTaxYear,
  yearsToShow = 30,
  existingOverrides,
}) => {
  // State for the working overrides
  const [workingOverrides, setWorkingOverrides] = useState<TaxableValueOverrides | null>(null);
  // State for currently editing cell
  const [editingYear, setEditingYear] = useState<number | null>(null);
  // State for edit input value (as displayed percentage string)
  const [editValue, setEditValue] = useState('');
  // State for validation error
  const [validationError, setValidationError] = useState<string | null>(null);
  // Ref for the input field to focus when row is clicked
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize state when modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkingOverrides(existingOverrides ? { byYear: { ...existingOverrides.byYear } } : null);
      setEditingYear(null);
      setEditValue('');
      setValidationError(null);
    }
  }, [isOpen, existingOverrides]);

  // Calculate projected values with current working overrides (live preview)
  const projectedValues = useMemo(() => {
    return projectTaxableValues(taxValueInputs, startingTaxYear, yearsToShow, workingOverrides);
  }, [taxValueInputs, startingTaxYear, yearsToShow, workingOverrides]);

  // Calculate original values (without any overrides) for comparison
  const originalValues = useMemo(() => {
    return projectTaxableValues(taxValueInputs, startingTaxYear, yearsToShow, null);
  }, [taxValueInputs, startingTaxYear, yearsToShow]);

  // Build table rows from projected values
  const tableRows: TaxableValueOverrideRow[] = useMemo(() => {
    return projectedValues.map((entry, index) => {
      const originalEntry = originalValues[index];
      const override = workingOverrides?.byYear[entry.taxYear];

      return {
        taxYear: entry.taxYear,
        growthRate: entry.growthRate,
        originalGrowthRate: originalEntry?.growthRate ?? entry.growthRate,
        taxableValue: entry.taxableValue,
        isOverridden: !!override,
      };
    });
  }, [projectedValues, originalValues, workingOverrides]);

  // Count of overridden values
  const overrideCount = useMemo(() => {
    return Object.keys(workingOverrides?.byYear ?? {}).length;
  }, [workingOverrides]);

  // Handle starting to edit a cell
  const handleStartEdit = useCallback((row: TaxableValueOverrideRow) => {
    setEditingYear(row.taxYear);
    // Display as percentage (e.g., "3.50" for 0.035)
    setEditValue((row.growthRate * 100).toFixed(2));
    setValidationError(null);
    // Focus the input after state updates
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, []);

  // Handle row click (to start editing)
  const handleRowClick = useCallback(
    (row: TaxableValueOverrideRow, e: React.MouseEvent) => {
      // Don't start editing if clicking on the reset button or if already editing
      if (
        editingYear === row.taxYear ||
        (e.target as HTMLElement).closest('button') ||
        (e.target as HTMLElement).tagName === 'BUTTON'
      ) {
        return;
      }
      handleStartEdit(row);
    },
    [editingYear, handleStartEdit]
  );

  // Handle input focus to select all text
  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  // Handle confirming an edit (on blur or Enter)
  const handleConfirmEdit = useCallback(() => {
    if (editingYear === null) return;

    const parsedRate = parseGrowthRateInput(editValue);
    if (parsedRate === null) {
      setValidationError('Please enter a valid percentage');
      return;
    }

    const error = validateTaxableValueGrowthRateOverride(parsedRate);
    if (error) {
      setValidationError(error);
      return;
    }

    // Find the original growth rate for this year
    const originalRow = tableRows.find((r) => r.taxYear === editingYear);
    if (!originalRow) return;

    // If the rate is the same as original (within tolerance), remove the override
    const epsilon = 0.0001;
    if (Math.abs(parsedRate - originalRow.originalGrowthRate) < epsilon) {
      if (workingOverrides) {
        setWorkingOverrides(removeTaxableValueOverride(workingOverrides, editingYear));
      }
    } else {
      // Set the override
      setWorkingOverrides(
        setTaxableValueGrowthRateOverride(
          workingOverrides,
          editingYear,
          originalRow.originalGrowthRate,
          parsedRate
        )
      );
    }

    setEditingYear(null);
    setEditValue('');
    setValidationError(null);
  }, [editingYear, editValue, workingOverrides, tableRows]);

  // Handle canceling an edit
  const handleCancelEdit = useCallback(() => {
    setEditingYear(null);
    setEditValue('');
    setValidationError(null);
  }, []);

  // Handle key press in edit input
  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleConfirmEdit();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleConfirmEdit, handleCancelEdit]
  );

  // Handle resetting a single row
  const handleResetRow = useCallback(
    (taxYear: number) => {
      if (workingOverrides) {
        setWorkingOverrides(removeTaxableValueOverride(workingOverrides, taxYear));
      }
    },
    [workingOverrides]
  );

  // Handle resetting all overrides
  const handleResetAll = useCallback(() => {
    setWorkingOverrides(null);
  }, []);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (!workingOverrides || Object.keys(workingOverrides.byYear).length === 0) {
      // No overrides to submit
      return;
    }

    onSubmit(workingOverrides);
    onClose();
  }, [workingOverrides, onSubmit, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Calculate value difference from original
  const calculateValueDiff = (row: TaxableValueOverrideRow): number => {
    const originalEntry = originalValues.find((e) => e.taxYear === row.taxYear);
    if (!originalEntry) return 0;
    return row.taxableValue - originalEntry.taxableValue;
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => (!e.open ? onClose() : undefined)}
      placement="center"
      size="xl"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxWidth="900px" maxHeight="80vh">
          <Dialog.Header>
            <Dialog.Title>Customize Growth Rates</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body overflow="auto">
            <VStack align="stretch" gap={4}>
              {/* Info text */}
              <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                <Text fontSize="sm" color="blue.800">
                  Click on any growth rate to edit it. When you change a year's rate, all subsequent
                  year values will automatically recalculate to show the cascading effect. Changes
                  will create a new configuration.
                </Text>
              </Box>

              {/* Validation error */}
              {validationError && (
                <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                  <Text fontSize="sm" color="red.600">
                    {validationError}
                  </Text>
                </Box>
              )}

              {/* Table */}
              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Tax Year</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Growth Rate</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Taxable Value</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">Diff from Original</Table.ColumnHeader>
                      <Table.ColumnHeader width="60px" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {tableRows.map((row) => {
                      const valueDiff = calculateValueDiff(row);
                      const hasImpact = Math.abs(valueDiff) > 1;

                      return (
                        <Table.Row
                          key={row.taxYear}
                          bg={row.isOverridden ? 'yellow.50' : hasImpact ? 'blue.50' : undefined}
                          borderLeftWidth={row.isOverridden ? '3px' : undefined}
                          borderLeftColor={row.isOverridden ? 'yellow.400' : undefined}
                          cursor={editingYear === row.taxYear ? 'default' : 'pointer'}
                          onClick={(e) => handleRowClick(row, e)}
                          _hover={
                            editingYear !== row.taxYear
                              ? {
                                  bg: row.isOverridden
                                    ? 'yellow.100'
                                    : hasImpact
                                      ? 'blue.200'
                                      : 'blue.100',
                                }
                              : undefined
                          }
                          transition="background-color 0.15s ease"
                        >
                          <Table.Cell fontWeight="medium">{row.taxYear}</Table.Cell>
                          <Table.Cell textAlign="right">
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              minHeight="32px"
                              width="100px"
                            >
                              {editingYear === row.taxYear ? (
                                <Input
                                  ref={inputRef}
                                  size="sm"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={handleEditKeyDown}
                                  onBlur={handleConfirmEdit}
                                  onFocus={handleInputFocus}
                                  autoFocus
                                  textAlign="right"
                                  width="100%"
                                />
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStartEdit(row)}
                                  fontWeight={row.isOverridden ? 'semibold' : 'normal'}
                                  color={row.isOverridden ? 'yellow.700' : undefined}
                                  width="100%"
                                  justifyContent="flex-end"
                                >
                                  {formatGrowthRate(row.growthRate)}
                                </Button>
                              )}
                            </Box>
                          </Table.Cell>
                          <Table.Cell textAlign="right" fontFamily="mono">
                            {formatCurrency(row.taxableValue)}
                          </Table.Cell>
                          <Table.Cell
                            textAlign="right"
                            color={
                              valueDiff > 0 ? 'green.600' : valueDiff < 0 ? 'red.600' : 'gray.500'
                            }
                            fontFamily="mono"
                          >
                            {hasImpact
                              ? `${valueDiff > 0 ? '+' : ''}${formatCurrency(valueDiff)}`
                              : '--'}
                          </Table.Cell>
                          <Table.Cell>
                            {row.isOverridden && (
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="gray"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetRow(row.taxYear);
                                }}
                                title="Reset to original"
                              >
                                <X size={14} />
                              </Button>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              </Box>

              {/* Override count and summary */}
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600">
                  {overrideCount === 0
                    ? 'No growth rates customized'
                    : `${overrideCount} growth rate${overrideCount > 1 ? 's' : ''} customized`}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Blue rows show cascading impact from overrides
                </Text>
              </HStack>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <Stack direction="row" gap={2} justify="space-between" width="100%">
              <Button
                variant="ghost"
                colorPalette="red"
                onClick={handleResetAll}
                disabled={overrideCount === 0}
              >
                <RefreshCw size={14} />
                <Text ml={1}>Reset All</Text>
              </Button>
              <HStack gap={2}>
                <Button variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button colorPalette="blue" onClick={handleSubmit} disabled={overrideCount === 0}>
                  Run Configuration
                </Button>
              </HStack>
            </Stack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
